import {
    Component,
    OnInit,
    Input,
    Output,
    EventEmitter,
    ViewChild,
    ElementRef,
    OnChanges,
    SimpleChanges,
} from '@angular/core';
import { FormControl } from '@angular/forms';

@Component({
    selector: 'app-number-input',
    templateUrl: './number-input.component.html',
    styleUrls: ['./number-input.component.scss'],
})
export class NumberInputComponent implements OnInit, OnChanges {
    @Input() public model: string | number = '0';
    @Input() public isError: boolean = false;
    @Input() public errorText: string = 'This is an example error message';
    @Input() public min?: number;
    @Input() public max?: number;
    @Input() public isDisabled: boolean = false;
    @Input() public isReadOnly: boolean = false;
    @Input() public small: boolean = false;
    @Input() public isSuffix: boolean = false;
    @Input() public suffix: string = '$';
    @Input() public isDecimal: boolean = false;
    @Input() public step: number = 0.001;
    @Input() public limit: number = 100;
    @Input() public isNegative: boolean = true;
    @Output() public modelChange = new EventEmitter<number>();
    @Output() public enterClicked = new EventEmitter<void>();

    val = new FormControl();
    private previousValue: string | number = '0';
    public pattern: string = '[0-9]*[,.]?[0-9]*$';

    public isFocused: boolean = false;
    public isAccepted: boolean = false;
    public isEmpty: boolean = true;
    private rounding: number = 2;

    @ViewChild('inputNumber') input!: ElementRef;

    ngOnChanges(changes: SimpleChanges) {
        if (changes.model) {
            this.model = changes.model.currentValue ?? '';
            this.val.setValue(this.model, { emitEvent: false });
        }
    }

    ngOnInit(): void {
        this.val = new FormControl({
            value: this.model,
            disabled: this.isDisabled,
        });

        this.setupPattern();
        this.listenToValueChanges();
    }

    private setupPattern() {
        if (!this.isDecimal) {
            this.previousValue = this.model;
            this.step = 1;
            this.pattern = this.isNegative ? '^[-]?[0-9]+$' : '^[0-9]+$';
        } else {
            this.previousValue = this.model;
            const decimalParts = this.step.toString().split('.');
            this.rounding = decimalParts[1]?.length || 2;
            this.pattern = this.isNegative
                ? '^[-]?[0-9]*[,.]?[0-9]*$'
                : '^[0-9]+([.,][0-9]+)?$';
        }
    }

    private listenToValueChanges() {
        this.val.valueChanges.subscribe((data) => this.numberValidate(data));
    }

    private numberValidate(value: string | number) {
        this.model = value;
        if (!this.val.value || !this.input) return; // Prevent error if input is not ready

        const inputElement = this.input.nativeElement; // Ensure input is available

        if (
            this.model.toString().charAt(0) === '0' &&
            inputElement.value.length > 1 &&
            !['.', ','].includes(inputElement.value.charAt(1))
        ) {
            this.model = inputElement.value = this.rmZero(Number(this.model));
        }

        if (this.val.errors?.pattern) {
            this.val.setValue(this.previousValue);
            this.model = this.previousValue;
            return;
        }

        this.previousValue = this.model;
        this.modelChange.emit(Number(this.model));
    }

    private rmZero(val: number): string {
        return Number(val).toString();
    }

    onFocus(): void {
        this.isFocused = true;
        this.isAccepted = false;
        if (Number(this.model) === 0) {
            this.model = '';
        }
    }

    onBlur(): void {
        this.isFocused = false;
        if (
            typeof this.model === 'string' &&
            this.isDecimal &&
            ['.', ','].includes(this.model.charAt(0))
        ) {
            this.isError = true;
        } else {
            this.isError = false;
        }
    }

    isInputEmpty(value: number): void {
        this.isEmpty = value == null || value === undefined;
        if (!this.isEmpty) this.isFocused = true;
    }

    public arrowStep(event: KeyboardEvent): void {
        let newValue: number;
        if (event.key === 'ArrowUp') {
            newValue = Number(this.val.value) + this.step;
        } else if (event.key === 'ArrowDown') {
            newValue = Number(this.val.value) - this.step;
        } else {
            return;
        }

        this.val.setValue(
            this.isDecimal ? newValue.toFixed(this.rounding) : newValue
        );
    }
}
